import { useState, useEffect, useCallback } from 'react';
import testingService, {
  TestScore,
  AdvancedCourse,
  TestScoreCreate,
  AdvancedCourseCreate,
} from '@/services/testingService';

export interface UseTestingDataReturn {
  testScores: TestScore[];
  advancedCourses: AdvancedCourse[];
  apSubjects: string[];
  ibSubjects: string[];
  satSubjectTests: string[];
  cambridgeSubjects: string[];
  isLoading: boolean;
  isCreatingTestScore: boolean;
  isCreatingAdvancedCourse: boolean;

  createTestScore: (data: TestScoreCreate) => Promise<void>;
  deleteTestScore: (testScoreId: number) => Promise<void>;
  createAdvancedCourse: (data: AdvancedCourseCreate) => Promise<void>;
  createAdvancedCoursesBulk: (courses: AdvancedCourseCreate[]) => Promise<void>;
  updateAdvancedCourse: (courseId: number, data: Partial<AdvancedCourseCreate>) => Promise<void>;
  deleteAdvancedCourse: (courseId: number) => Promise<void>;
  refreshData: () => Promise<void>;
}

export const useTestingData = (): UseTestingDataReturn => {
  const [testScores, setTestScores] = useState<TestScore[]>([]);
  const [advancedCourses, setAdvancedCourses] = useState<AdvancedCourse[]>([]);
  const [apSubjects, setApSubjects] = useState<string[]>([]);
  const [ibSubjects, setIbSubjects] = useState<string[]>([]);
  const [satSubjectTests, setSatSubjectTests] = useState<string[]>([]);
  const [cambridgeSubjects, setCambridgeSubjects] = useState<string[]>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isCreatingTestScore, setIsCreatingTestScore] = useState(false);
  const [isCreatingAdvancedCourse, setIsCreatingAdvancedCourse] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [
        testScoresData,
        advancedCoursesData,
        apSubjectsData,
        ibSubjectsData,
        satSubjectsData,
        cambridgeSubjectsData,
      ] = await Promise.all([
        testingService.getTestScores(),
        testingService.getAdvancedCourses(),
        testingService.getAPSubjects(),
        testingService.getIBSubjects(),
        testingService.getSATSubjectTests(),
        testingService.getCambridgeSubjects(),
      ]);

      setTestScores(testScoresData.test_scores);
      setAdvancedCourses(advancedCoursesData.advanced_courses);
      setApSubjects(apSubjectsData.subjects);
      setIbSubjects(ibSubjectsData.subjects);
      setSatSubjectTests(satSubjectsData.subjects);
      setCambridgeSubjects(cambridgeSubjectsData.subjects);
    } catch (error) {
      console.error('Error fetching testing data:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const createTestScore = async (data: TestScoreCreate) => {
    setIsCreatingTestScore(true);
    try {
      const newScore = await testingService.createTestScore(data);
      setTestScores((prev) => [...prev, newScore]);
    } finally {
      setIsCreatingTestScore(false);
    }
  };

  const deleteTestScore = async (testScoreId: number) => {
    await testingService.deleteTestScore(testScoreId);
    setTestScores((prev) => prev.filter((score) => score.id !== testScoreId));
  };

  const createAdvancedCourse = async (data: AdvancedCourseCreate) => {
    setIsCreatingAdvancedCourse(true);
    try {
      const newCourse = await testingService.createAdvancedCourse(data);
      setAdvancedCourses((prev) => [...prev, newCourse]);
    } finally {
      setIsCreatingAdvancedCourse(false);
    }
  };

  const createAdvancedCoursesBulk = async (courses: AdvancedCourseCreate[]) => {
    setIsCreatingAdvancedCourse(true);
    try {
      const result = await testingService.createAdvancedCoursesBulk(courses);
      setAdvancedCourses((prev) => [...prev, ...result.advanced_courses]);
    } finally {
      setIsCreatingAdvancedCourse(false);
    }
  };

  const updateAdvancedCourse = async (
    courseId: number,
    data: Partial<AdvancedCourseCreate>
  ) => {
    const updatedCourse = await testingService.updateAdvancedCourse(courseId, data);
    setAdvancedCourses((prev) =>
      prev.map((course) => (course.id === courseId ? updatedCourse : course))
    );
  };

  const deleteAdvancedCourse = async (courseId: number) => {
    await testingService.deleteAdvancedCourse(courseId);
    setAdvancedCourses((prev) => prev.filter((course) => course.id !== courseId));
  };

  return {
    testScores,
    advancedCourses,
    apSubjects,
    ibSubjects,
    satSubjectTests,
    cambridgeSubjects,
    isLoading,
    isCreatingTestScore,
    isCreatingAdvancedCourse,
    createTestScore,
    deleteTestScore,
    createAdvancedCourse,
    createAdvancedCoursesBulk,
    updateAdvancedCourse,
    deleteAdvancedCourse,
    refreshData: fetchData,
  };
};

export default useTestingData;
